import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Modal from '../../components/UI/Modal';
import {
  User,
  ArrowLeft,
  Edit,
  Award,
  Calendar,
  MapPin,
  Mail,
  Phone,
  Shield,
  FileText,
  FileDown,
  FileWarning,
  Layers,
  CheckCircle,
  RotateCcw,
  X
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { membersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const MemberDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailCard = styled(Card)`
  padding: 2rem;
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const DetailIcon = styled.div`
  width: 50px;
  height: 50px;
  background-color: ${props => props.theme.colors.primary + '20'};
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.primary};
`;

const DetailTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${props => props.theme.typography.fontSize.md};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 2rem 0 1rem;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const InfoItem = styled.div`
  padding: 1rem;
  background-color: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const InfoLabel = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.text.secondary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const PhotoWrapper = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
  flex-wrap: wrap;
`;

const PhotoPreview = styled.div`
  width: 130px;
  height: 130px;
  border-radius: ${props => props.theme.borderRadius.lg};
  overflow: hidden;
  border: 2px solid ${props => props.theme.colors.primary};
  background-color: ${props => props.theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${props => props.theme.colors.text.secondary};
  background-color: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const List = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  color: ${props => props.theme.colors.text.primary};
`;

const TrainingHistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const TrainingHistoryItem = styled.div`
  padding: 1rem;
  background: ${props => props.theme.colors.background.secondary};
  border-radius: ${props => props.theme.borderRadius.md};
  border-left: 3px solid ${props => props.theme.colors.primary};
`;

const TrainingHistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const TrainingTitle = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.fontSize.sm};
  flex: 1;
`;

const TrainingStatus = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: 0.75rem;
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  text-transform: capitalize;
  background: ${props => {
    if (props.status === 'completed') return props.theme.colors.success + '20';
    if (props.status === 'attended') return props.theme.colors.primary + '20';
    return props.theme.colors.text.secondary + '20';
  }};
  color: ${props => {
    if (props.status === 'completed') return props.theme.colors.success;
    if (props.status === 'attended') return props.theme.colors.primary;
    return props.theme.colors.text.secondary;
  }};
`;

const TrainingDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.8rem;
  color: ${props => props.theme.colors.text.secondary};
`;

const TrainingDetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const RAW_API_URL = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api').replace(/\/+$/, '');
const API_ORIGIN = RAW_API_URL.endsWith('/api') ? RAW_API_URL.slice(0, -4) : RAW_API_URL;
const API_BASE_URL = API_ORIGIN;
const MEDIA_ENDPOINT = `${API_ORIGIN}/api/media`;

const resolveFileUrl = (input) => {
  if (!input) return '';
  const path = String(input);

  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  const normalize = (value) => {
    let normalized = value.replace(/\\/g, '/');

    if (normalized.startsWith('/storage/')) {
      normalized = normalized.replace(/^\/storage\//, '');
    } else if (normalized.startsWith('storage/')) {
      normalized = normalized.replace(/^storage\//, '');
    }

    if (normalized.startsWith('public/')) {
      normalized = normalized.replace(/^public\//, '');
    }

    return normalized;
  };

  if (path.startsWith('/')) {
    if (path.startsWith('/storage/')) {
      const cleaned = normalize(path);
      return `${MEDIA_ENDPOINT}?path=${encodeURIComponent(cleaned)}`;
    }

    return `${API_BASE_URL}${path}`;
  }

  const cleaned = normalize(path);
  return `${MEDIA_ENDPOINT}?path=${encodeURIComponent(cleaned)}`;
};

const FieldRow = ({ label, value }) => {
  let displayValue = 'Not provided';

  if (Array.isArray(value)) {
    displayValue = value.length ? value.join(', ') : 'Not provided';
  } else if (value !== undefined && value !== null && String(value).trim() !== '') {
    displayValue = value;
  }

  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <strong>{label}:</strong> {displayValue}
    </div>
  );
};

const MemberDetail = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission } = useAuth();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const [resubmitNotes, setResubmitNotes] = useState('');

  useEffect(() => {
    const loadMember = async () => {
      setLoading(true);
      try {
        const response = await membersAPI.getMember(id);
        setMember(response.data);
      } catch (error) {
        console.error('Failed to load member details', error);
        toast.error('Unable to load member details');
        navigate('/app/members');
      } finally {
        setLoading(false);
      }
    };

    loadMember();
  }, [id, navigate]);

  const memberFullName = useMemo(() => {
    if (!member) return '';
    const { first_name, middle_name, last_name } = member;
    return [first_name, middle_name, last_name].filter(Boolean).join(' ');
  }, [member]);

  const primaryEmail = member?.email || 'Not provided';
  const primaryPhone = member?.phone || 'Not provided';
  const regionName = member?.region?.name || 'Not provided';
  const categoryName = member?.member_category?.name || 'Not provided';
  const memberStatus = member?.membership_status ? member.membership_status.replace('_', ' ') : 'Not provided';
  const approvalStatus = member?.approval_status ? member.approval_status.replace('_', ' ') : 'Not provided';
  const membershipType = member?.membership_type ? member.membership_type.replace('_', ' ') : 'Not provided';

  const trainings = member?.trainings || [];
  const events = member?.events || [];
  const profilePhotoUrl = resolveFileUrl(member?.profile_photo_url);
  const idDocumentUrl = resolveFileUrl(member?.id_document_url);
  const canApprove = hasPermission('approve_members') && member && member.approval_status !== 'approved';
  const registrationSubmission = member?.latest_submission || null;
  const registrationFormData = registrationSubmission?.form_data || {};
  const registrationFiles = useMemo(() => {
    if (!registrationSubmission?.files) {
      return {};
    }

    return Object.entries(registrationSubmission.files).reduce((acc, [key, value]) => {
      if (!value) {
        return acc;
      }
      acc[key] = resolveFileUrl(value);
      return acc;
    }, {});
  }, [registrationSubmission]);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) {
      return value.map(item => (typeof item === 'string' ? item.trim() : item)).filter(Boolean);
    }
    if (typeof value === 'string' && value.trim().length) {
      return value.split(',').map(item => item.trim()).filter(Boolean);
    }
    return [];
  };

  const registrationTrainings = normalizeArray(
    registrationFormData.trainings ??
    registrationFormData.training ??
    registrationFormData.training_interests
  );

  const registrationInterests = normalizeArray(
    registrationFormData.thematic_areas ??
    registrationFormData.interests ??
    registrationFormData.focus_areas
  );

  const registrationLanguages = normalizeArray(
    registrationFormData.languages_spoken ??
    registrationFormData.languages ??
    registrationFormData.languages_of_communication
  );

  const registrationSkills =
    registrationFormData.skills ??
    registrationFormData.skills_expertise ??
    registrationFormData.skillset ??
    '';

  const normalizeBoolean = (value) => {
    if (typeof value === 'boolean') return value;
    if (value === undefined || value === null) return null;
    const normalized = String(value).toLowerCase();
    if (['1', 'true', 'yes', 'y'].includes(normalized)) return true;
    if (['0', 'false', 'no', 'n'].includes(normalized)) return false;
    return null;
  };

  const memberHasDisability = normalizeBoolean(member?.has_disability);
  const registrationHasDisability = normalizeBoolean(
    registrationFormData.has_disability ??
    registrationFormData.physical_disability
  );
  const effectiveHasDisability =
    memberHasDisability !== null && memberHasDisability !== undefined
      ? memberHasDisability
      : registrationHasDisability;
  const disabilityType =
    member?.disability_type ||
    registrationFormData.disability_type ||
    registrationFormData.disability_type_other ||
    registrationFormData.disability_other ||
    '';
  const memberDisabilityLabel =
    effectiveHasDisability === null || effectiveHasDisability === undefined
      ? 'Not provided'
      : effectiveHasDisability
        ? `Yes${disabilityType ? ` (${disabilityType})` : ''}`
        : 'No';
  const registrationDisabilityType =
    registrationFormData.disability_type ||
    registrationFormData.disability_type_other ||
    registrationFormData.disability_other ||
    '';
  const registrationDisabilityLabel =
    registrationHasDisability === null || registrationHasDisability === undefined
      ? ''
      : registrationHasDisability
        ? `Yes${registrationDisabilityType ? ` (${registrationDisabilityType})` : ''}`
        : 'No';
  const registrationResumeUrl =
    registrationFiles.cv ||
    (registrationFormData.cv_url ? resolveFileUrl(registrationFormData.cv_url) : '');
  const memberResumeUrl = member?.cv_url
    ? resolveFileUrl(member.cv_url)
    : registrationResumeUrl;

  const handleApprove = async () => {
    if (!member || !canApprove || isApproving) return;
    try {
      setIsApproving(true);
      await membersAPI.approve(member.id, {});
      toast.success('Member approved and activated');
      setMember(prev => prev ? ({
        ...prev,
        approval_status: 'approved',
        membership_status: 'active',
        membership_date: prev.membership_date || new Date().toISOString()
      }) : prev);
    } catch (error) {
      console.error('Failed to approve member', error);
      toast.error(error.response?.data?.message || 'Failed to approve member');
    } finally {
      setIsApproving(false);
    }
  };

  const handleRequestResubmit = async () => {
    if (!member || isResubmitting) return;

    const notes = resubmitNotes.trim();
    if (!notes) {
      toast.error('Please provide notes before sending the resubmission request.');
      return;
    }

    try {
      setIsResubmitting(true);
      await membersAPI.requestResubmission(member.id, { notes });
      toast.success('Resubmission requested.');
      setMember(prev => prev ? ({
        ...prev,
        approval_status: 'draft',
        membership_status: 'pending'
      }) : prev);
      setShowResubmitModal(false);
      setResubmitNotes('');
    } catch (error) {
      console.error('Failed to request resubmission', error);
      toast.error(error.response?.data?.message || 'Failed to request resubmission');
    } finally {
      setIsResubmitting(false);
    }
  };

  const handleToggleMembershipStatus = async () => {
    if (!member || isUpdatingStatus) return;

    const nextStatus = member.membership_status === 'active' ? 'inactive' : 'active';

    try {
      setIsUpdatingStatus(true);
      const response = await membersAPI.setStatus(member.id, { status: nextStatus });
      const updated = response.data?.data || response.data;

      setMember(prev => prev ? ({
        ...prev,
        ...updated,
        membership_status: updated?.membership_status ?? nextStatus,
        membership_date: updated?.membership_date ?? prev.membership_date,
      }) : prev);

      toast.success(`Member marked as ${nextStatus === 'active' ? 'active' : 'inactive'}.`);
    } catch (error) {
      console.error('Failed to update membership status', error);
      toast.error(error.response?.data?.message || 'Failed to update membership status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const openResubmitModal = () => {
    if (!member) return;
    setResubmitNotes('');
    setShowResubmitModal(true);
  };
  
  return (
    <MemberDetailContainer theme={theme}>
      <Header theme={theme}>
        <Button 
          variant="ghost" 
          onClick={() => navigate('/app/members')}
          leftIcon={<ArrowLeft size={16} />}
        >
          Back to Members
        </Button>
        <Title theme={theme}>{loading ? 'Loading member...' : memberFullName}</Title>
      </Header>
      
      {loading ? (
        <Card theme={theme} style={{ padding: '3rem', textAlign: 'center' }}>
          Loading member details...
        </Card>
      ) : (
        <>
      <DetailGrid theme={theme}>
        <DetailCard theme={theme}>
          <DetailHeader theme={theme}>
            <DetailIcon theme={theme}>
              <User size={24} />
            </DetailIcon>
            <DetailTitle theme={theme}>Personal Information</DetailTitle>
          </DetailHeader>
          
              <PhotoWrapper>
                {profilePhotoUrl ? (
                  <PhotoPreview theme={theme}>
                    <img src={profilePhotoUrl} alt={memberFullName} />
                  </PhotoPreview>
                ) : (
                  <EmptyState theme={theme} style={{ width: '100%' }}>
                    <FileWarning size={20} /> No profile photo uploaded
                  </EmptyState>
                )}
              </PhotoWrapper>

              <InfoGrid theme={theme} style={{ marginTop: '1.5rem' }}>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Member ID</InfoLabel>
                  <InfoValue theme={theme}>{member?.member_id || 'Not assigned'}</InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Full Name</InfoLabel>
                  <InfoValue theme={theme}>{memberFullName || 'Not provided'}</InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Gender</InfoLabel>
                  <InfoValue theme={theme}>{member?.gender || 'Not provided'}</InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Date of Birth</InfoLabel>
                  <InfoValue theme={theme}>
                    {member?.date_of_birth ? new Date(member.date_of_birth).toLocaleDateString() : 'Not provided'}
                  </InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Membership Type</InfoLabel>
                  <InfoValue theme={theme}>{membershipType}</InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Membership Status</InfoLabel>
                  <InfoValue theme={theme}>{memberStatus}</InfoValue>
            </InfoItem>
          </InfoGrid>
        </DetailCard>
        
        <DetailCard theme={theme}>
          <DetailHeader theme={theme}>
            <DetailIcon theme={theme}>
                  <Shield size={24} />
            </DetailIcon>
                <DetailTitle theme={theme}>Contact & Approval</DetailTitle>
          </DetailHeader>
          
          <InfoGrid theme={theme}>
            <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Primary Email</InfoLabel>
                  <InfoValue theme={theme}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Mail size={14} /> {primaryEmail}
                    </div>
                  </InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Primary Phone</InfoLabel>
                  <InfoValue theme={theme}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Phone size={14} /> {primaryPhone}
                    </div>
                  </InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Region</InfoLabel>
                  <InfoValue theme={theme}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <MapPin size={14} /> {regionName}
                    </div>
                  </InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Member Category</InfoLabel>
                  <InfoValue theme={theme}>{categoryName}</InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Approval Status</InfoLabel>
                  <InfoValue theme={theme}>{approvalStatus}</InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Membership Date</InfoLabel>
                  <InfoValue theme={theme}>
                    {member?.membership_date
                      ? new Date(member.membership_date).toLocaleDateString()
                      : 'Not provided'}
                  </InfoValue>
            </InfoItem>
          </InfoGrid>

              <div style={{ marginTop: '1.5rem' }}>
                <DetailHeader theme={theme} style={{ marginBottom: '1rem' }}>
                  <DetailIcon theme={theme}>
                    <FileText size={20} />
                  </DetailIcon>
                  <DetailTitle theme={theme}>Documents</DetailTitle>
                </DetailHeader>

                {idDocumentUrl ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<FileDown size={14} />}
                    onClick={() => window.open(idDocumentUrl, '_blank')}
                  >
                    View ID Document
                  </Button>
                ) : (
                  <EmptyState theme={theme}>
                    <FileWarning size={18} /> No ID document uploaded
                  </EmptyState>
                )}
              </div>
        </DetailCard>
      </DetailGrid>
      
      <DetailGrid theme={theme}>
        <DetailCard theme={theme}>
          <DetailHeader theme={theme}>
            <DetailIcon theme={theme}>
              <Award size={24} />
            </DetailIcon>
            <DetailTitle theme={theme}>Professional & Additional Information</DetailTitle>
          </DetailHeader>

          <InfoGrid theme={theme}>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Organization</InfoLabel>
              <InfoValue theme={theme}>{member?.organization_name || 'Not provided'}</InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Occupation / Position</InfoLabel>
              <InfoValue theme={theme}>{member?.occupation || member?.position_title || 'Not provided'}</InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Languages</InfoLabel>
              <InfoValue theme={theme}>
                {member?.languages_spoken?.length
                  ? member.languages_spoken.join(', ')
                  : registrationLanguages.length
                    ? registrationLanguages.join(', ')
                    : 'Not specified'}
              </InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Areas of Interest</InfoLabel>
              <InfoValue theme={theme}>
                {member?.thematic_areas?.length
                  ? member.thematic_areas.join(', ')
                  : registrationInterests.length
                    ? registrationInterests.join(', ')
                    : 'Not specified'}
              </InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Skills & Expertise</InfoLabel>
              <InfoValue theme={theme}>
                {member?.skills || registrationSkills || 'Not provided'}
              </InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Interested Trainings</InfoLabel>
              <InfoValue theme={theme}>
                {Array.isArray(member?.interested_trainings) && member.interested_trainings.length
                  ? member.interested_trainings.join(', ')
                  : registrationTrainings
                    ? registrationTrainings
                    : 'Not specified'}
              </InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Physical Disability</InfoLabel>
              <InfoValue theme={theme}>{memberDisabilityLabel}</InfoValue>
            </InfoItem>
            <InfoItem theme={theme}>
              <InfoLabel theme={theme}>Resume / CV</InfoLabel>
              <InfoValue theme={theme}>
                {memberResumeUrl ? (
                  <a
                    href={memberResumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: theme.colors.primary }}
                  >
                    Download CV
                  </a>
                ) : (
                  'Not uploaded'
                )}
              </InfoValue>
            </InfoItem>
          </InfoGrid>

          <SectionTitle theme={theme} style={{ marginTop: '2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FileText size={20} />
            Biography / About
          </SectionTitle>
          <p style={{ color: theme.colors.text.secondary, marginTop: '0.5rem' }}>
            {member?.bio || registrationFormData?.bio || 'Not provided'}
          </p>

            <SectionTitle theme={theme} style={{ marginTop: '2rem' }}>
              <Phone size={20} />
              Emergency Contact
            </SectionTitle>
            <InfoGrid theme={theme}>
              <InfoItem theme={theme}>
                <InfoLabel theme={theme}>Name</InfoLabel>
                <InfoValue theme={theme}>{member?.emergency_contact_name || registrationFormData?.emergency_contact_name || 'Not provided'}</InfoValue>
              </InfoItem>
              <InfoItem theme={theme}>
                <InfoLabel theme={theme}>Phone</InfoLabel>
                <InfoValue theme={theme}>{member?.emergency_contact_phone || registrationFormData?.emergency_contact_phone || 'Not provided'}</InfoValue>
              </InfoItem>
              <InfoItem theme={theme}>
                <InfoLabel theme={theme}>Relationship</InfoLabel>
                <InfoValue theme={theme}>{member?.emergency_contact_relationship || registrationFormData?.emergency_contact_relationship || 'Not provided'}</InfoValue>
              </InfoItem>
            </InfoGrid>
        </DetailCard>

        {registrationSubmission && (
          <DetailCard theme={theme}>
            <DetailHeader theme={theme}>
              <DetailIcon theme={theme}>
                <FileText size={24} />
              </DetailIcon>
              <DetailTitle theme={theme}>Latest Registration Submission</DetailTitle>
            </DetailHeader>

            <div style={{ marginBottom: '0.75rem', color: theme.colors.text.secondary }}>
              Submitted on{' '}
              {registrationSubmission.submitted_at
                ? new Date(registrationSubmission.submitted_at).toLocaleString()
                : 'Unknown'}
            </div>

            <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, marginTop: 0 }}>
              View the full application exactly as submitted by the member, including all form builder fields.
            </p>

            <Button
              variant="outline"
              size="small"
              onClick={() => navigate(`/app/members/${id}/application`)}
              leftIcon={<FileText size={14} />}
              style={{ marginTop: '0.75rem' }}
            >
              Open Full Application
            </Button>

            {(registrationFiles.profile_photo || registrationFiles.id_document) && (
              <div
                style={{
                  marginTop: '1.5rem',
                  display: 'flex',
                  gap: '1rem',
                  flexWrap: 'wrap'
                }}
              >
                {registrationFiles.profile_photo && (
                  <a
                    href={registrationFiles.profile_photo}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: theme.colors.primary + '15',
                      color: theme.colors.primary,
                      padding: '0.5rem 0.85rem',
                      borderRadius: theme.borderRadius.sm,
                      textDecoration: 'none',
                      fontWeight: theme.typography.fontWeight.medium,
                    }}
                  >
                    View Uploaded Photo
                  </a>
                )}
                {registrationFiles.id_document && (
                  <a
                    href={registrationFiles.id_document}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: theme.colors.primary + '15',
                      color: theme.colors.primary,
                      padding: '0.5rem 0.85rem',
                      borderRadius: theme.borderRadius.sm,
                      textDecoration: 'none',
                      fontWeight: theme.typography.fontWeight.medium,
                    }}
                  >
                    View ID Document
                  </a>
                )}
                {registrationResumeUrl && (
                  <a
                    href={registrationResumeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: theme.colors.primary + '15',
                      color: theme.colors.primary,
                      padding: '0.5rem 0.85rem',
                      borderRadius: theme.borderRadius.sm,
                      textDecoration: 'none',
                      fontWeight: theme.typography.fontWeight.medium,
                    }}
                  >
                    Download Submitted CV
                  </a>
                )}
              </div>
            )}
          </DetailCard>
        )}
      </DetailGrid>

        <DetailCard theme={theme}>
          <DetailHeader theme={theme}>
            <DetailIcon theme={theme}>
              <Layers size={24} />
            </DetailIcon>
            <DetailTitle theme={theme}>Recorded Activities</DetailTitle>
          </DetailHeader>

          {trainings.length === 0 && events.length === 0 ? (
            <EmptyState theme={theme} style={{ marginTop: '1.5rem' }}>
              No recorded activities for this member yet.
            </EmptyState>
          ) : (
            <>
              <InfoGrid theme={theme}>
                <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Trainings Attended</InfoLabel>
                  <InfoValue theme={theme}>{trainings.length}</InfoValue>
                </InfoItem>
                <InfoItem theme={theme}>
                  <InfoLabel theme={theme}>Events Participated</InfoLabel>
                  <InfoValue theme={theme}>{events.length}</InfoValue>
                </InfoItem>
              </InfoGrid>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1.5rem' }}>
                <div>
                  <DetailTitle 
                    theme={theme} 
                    style={{ fontSize: theme.typography.fontSize.md, marginBottom: '0.75rem' }}
                  >
                    Training History
                  </DetailTitle>
                  {trainings.length > 0 ? (
                    <TrainingHistoryList theme={theme}>
                      {trainings.map(training => (
                        <TrainingHistoryItem key={training.id} theme={theme}>
                          <TrainingHistoryHeader>
                            <TrainingTitle theme={theme}>
                              {training.training_program?.title || 'Training Program'}
                            </TrainingTitle>
                            <TrainingStatus status={training.attendance_status} theme={theme}>
                              {training.attendance_status || 'completed'}
                            </TrainingStatus>
                          </TrainingHistoryHeader>
                          <TrainingDetails theme={theme}>
                            {training.completion_date && (
                              <TrainingDetailItem>
                                <Calendar size={12} />
                                {new Date(training.completion_date).toLocaleDateString()}
                              </TrainingDetailItem>
                            )}
                            {training.training_program?.training_type && (
                              <TrainingDetailItem>
                                {training.training_program.training_type.replace('_', ' ')}
                              </TrainingDetailItem>
                            )}
                            {training.attendance_percentage && (
                              <TrainingDetailItem>
                                <CheckCircle size={12} />
                                {training.attendance_percentage}% attendance
                              </TrainingDetailItem>
                            )}
                          </TrainingDetails>
                        </TrainingHistoryItem>
                      ))}
                    </TrainingHistoryList>
                  ) : (
                    <EmptyState theme={theme}>No training history yet.</EmptyState>
                  )}
                </div>
                <div>
                  <DetailTitle 
                    theme={theme} 
                    style={{ fontSize: theme.typography.fontSize.md, marginBottom: '0.75rem' }}
                  >
                    Events
                  </DetailTitle>
                  {events.length > 0 ? (
                    <List theme={theme}>
                      {events.map(event => (
                        <li key={event.id || event.title}>
                          {event.title || 'Event'}{' '}
                          {event.pivot?.participated_at && (
                            <span style={{ color: theme.colors.text.secondary }}>
                              (Attended on {new Date(event.pivot.participated_at).toLocaleDateString()})
                            </span>
                          )}
                        </li>
                      ))}
                    </List>
                  ) : (
                    <EmptyState theme={theme}>No event participation recorded.</EmptyState>
                  )}
                </div>
              </div>
            </>
          )}
        </DetailCard>
        
        <ButtonGroup theme={theme}>
            <Button variant="outline" onClick={() => navigate('/app/members')}>
            Back to Members
          </Button>
            {canApprove && (
              <Button
                variant="primary"
                leftIcon={<CheckCircle size={16} />}
                onClick={handleApprove}
                disabled={isApproving}
              >
                {isApproving ? 'Approving...' : 'Approve Member'}
              </Button>
            )}
          {member && member.approval_status === 'approved' && hasPermission('approve_members') && (
            <Button
              variant={member.membership_status === 'active' ? 'danger' : 'secondary'}
              leftIcon={member.membership_status === 'active' ? <X size={16} /> : <CheckCircle size={16} />}
              onClick={handleToggleMembershipStatus}
              disabled={isUpdatingStatus}
            >
              {isUpdatingStatus
                ? 'Updating...'
                : member.membership_status === 'active'
                  ? 'Mark as Inactive'
                  : 'Activate Member'}
            </Button>
          )}
            {canApprove && (
              <Button
                variant="warning"
                leftIcon={<RotateCcw size={16} />}
                onClick={openResubmitModal}
              >
                Request Resubmit
              </Button>
            )}
            {hasPermission('edit_members') && (
              <Button 
                variant="primary" 
                leftIcon={<Edit size={16} />}
                onClick={() => navigate(`/app/members/${id}/complete`)}
              >
                Edit Member
              </Button>
            )}
        </ButtonGroup>
        </>
      )}
      <Modal
        isOpen={showResubmitModal}
        onClose={() => {
          if (!isResubmitting) {
            setShowResubmitModal(false);
            setResubmitNotes('');
          }
        }}
        title="Request Resubmission"
        size="small"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ margin: 0, color: theme.colors.text.secondary }}>
            Provide clear guidance so the applicant knows what to update before resubmitting.
          </p>
          <textarea
            value={resubmitNotes}
            onChange={(e) => setResubmitNotes(e.target.value)}
            placeholder="Example: Please upload a clearer ID scan and complete the organization details."
            rows={4}
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '8px',
              border: `1px solid ${theme.colors.border.medium}`,
              fontFamily: theme.typography.fontFamily.primary,
              fontSize: theme.typography.fontSize.base,
              resize: 'vertical'
            }}
            disabled={isResubmitting}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <Button
              variant="secondary"
              onClick={() => {
                if (!isResubmitting) {
                  setShowResubmitModal(false);
                  setResubmitNotes('');
                }
              }}
              disabled={isResubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={handleRequestResubmit}
              disabled={isResubmitting}
            >
              {isResubmitting ? 'Sending...' : 'Send Request'}
            </Button>
          </div>
        </div>
      </Modal>

    </MemberDetailContainer>
  );
};

export default MemberDetail;
